import { modifier } from 'ember-modifier';

interface PanZoomState {
  scale: number;
  translateX: number;
  translateY: number;
  isDragging: boolean;
  startX: number;
  startY: number;
  initialTranslateX: number;
  initialTranslateY: number;
  startDistance: number;
  initialScale: number;
}

const MIN_SCALE = 1;
const MAX_SCALE = 4;

/**
 * Pan-zoom modifier for image viewing
 *
 * Supports:
 * - Touch: pinch-to-zoom, single-finger pan when zoomed
 * - Mouse: wheel zoom, drag-to-pan when zoomed
 * - Double-tap/click to reset zoom
 */
const panZoom = modifier((element: HTMLElement) => {
  const state: PanZoomState = {
    scale: 1,
    translateX: 0,
    translateY: 0,
    isDragging: false,
    startX: 0,
    startY: 0,
    initialTranslateX: 0,
    initialTranslateY: 0,
    startDistance: 0,
    initialScale: 1,
  };

  // Set transform-origin to top-left for correct zoom-toward-mouse behavior
  element.style.transformOrigin = '0 0';

  const updateTransform = (): void => {
    element.style.transform = `translate(${state.translateX}px, ${state.translateY}px) scale(${state.scale})`;
    // Update cursor based on zoom state
    if (!state.isDragging) {
      element.style.cursor = state.scale > 1 ? 'grab' : '';
    }
  };

  const reset = (): void => {
    state.scale = 1;
    state.translateX = 0;
    state.translateY = 0;
    updateTransform();
  };

  // Touch handlers
  const handleTouchStart = (e: TouchEvent): void => {
    if (e.touches.length === 1) {
      // Single finger - prepare for pan
      const touch = e.touches[0];
      if (!touch) return;

      state.isDragging = true;
      state.startX = touch.clientX;
      state.startY = touch.clientY;
      state.initialTranslateX = state.translateX;
      state.initialTranslateY = state.translateY;
    } else if (e.touches.length === 2) {
      // Two fingers - prepare for pinch zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      if (!touch1 || !touch2) return;

      const dx = touch1.clientX - touch2.clientX;
      const dy = touch1.clientY - touch2.clientY;
      state.startDistance = Math.sqrt(dx * dx + dy * dy);
      state.initialScale = state.scale;
      state.isDragging = false;
    }
  };

  const handleTouchMove = (e: TouchEvent): void => {
    if (e.touches.length === 1 && state.isDragging && state.scale > 1) {
      // Pan with single finger when zoomed
      const touch = e.touches[0];
      if (!touch) return;

      const dx = touch.clientX - state.startX;
      const dy = touch.clientY - state.startY;
      state.translateX = state.initialTranslateX + dx;
      state.translateY = state.initialTranslateY + dy;
      updateTransform();
    } else if (e.touches.length === 2) {
      // Pinch zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      if (!touch1 || !touch2) return;

      const dx = touch1.clientX - touch2.clientX;
      const dy = touch1.clientY - touch2.clientY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (state.startDistance > 0) {
        const newScale = Math.min(
          MAX_SCALE,
          Math.max(
            MIN_SCALE,
            state.initialScale * (distance / state.startDistance)
          )
        );

        if (newScale !== state.scale) {
          // Calculate midpoint between fingers in element coordinate space
          const rect = element.getBoundingClientRect();
          const midX = (touch1.clientX + touch2.clientX) / 2 - rect.left;
          const midY = (touch1.clientY + touch2.clientY) / 2 - rect.top;

          // Point under midpoint in unscaled image coordinates
          const pointX = (midX - state.translateX) / state.scale;
          const pointY = (midY - state.translateY) / state.scale;

          // Apply new scale
          state.scale = newScale;

          // Adjust translation to zoom toward midpoint
          state.translateX = midX - pointX * state.scale;
          state.translateY = midY - pointY * state.scale;
        }
        updateTransform();
      }
    }
  };

  const handleTouchEnd = (): void => {
    state.isDragging = false;
    state.startDistance = 0;
  };

  // Mouse handlers
  const handleWheel = (e: WheelEvent): void => {
    e.preventDefault();

    const rect = element.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Calculate zoom relative to mouse position (smoother zoom with smaller delta)
    const scaleDelta = e.deltaY > 0 ? 0.95 : 1.05;
    const newScale = Math.min(
      MAX_SCALE,
      Math.max(MIN_SCALE, state.scale * scaleDelta)
    );

    if (newScale === state.scale) return;

    // Calculate the point under mouse in unscaled image coordinate space
    // This works in the element's transformed coordinate space, not affected by container position
    const pointX = (mouseX - state.translateX) / state.scale;
    const pointY = (mouseY - state.translateY) / state.scale;

    // Apply new scale
    state.scale = newScale;

    // Adjust translation so the same point stays under mouse
    state.translateX = mouseX - pointX * state.scale;
    state.translateY = mouseY - pointY * state.scale;

    // Reset translation when zooming back to 1x to center the image
    if (state.scale === 1) {
      state.translateX = 0;
      state.translateY = 0;
    }

    updateTransform();
  };

  const handleMouseDown = (e: MouseEvent): void => {
    if (state.scale <= 1) return;
    e.preventDefault(); // Prevent text selection

    state.isDragging = true;
    state.startX = e.clientX;
    state.startY = e.clientY;
    state.initialTranslateX = state.translateX;
    state.initialTranslateY = state.translateY;

    element.style.cursor = 'grabbing';

    // Attach to window to catch events outside element
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent): void => {
    if (!state.isDragging) return;

    const dx = e.clientX - state.startX;
    const dy = e.clientY - state.startY;
    state.translateX = state.initialTranslateX + dx;
    state.translateY = state.initialTranslateY + dy;
    updateTransform();
  };

  const handleMouseUp = (): void => {
    if (!state.isDragging) return;

    state.isDragging = false;
    element.style.cursor = state.scale > 1 ? 'grab' : '';

    // Remove window listeners
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  };

  // Double-click/tap to reset
  const handleDoubleClick = (): void => {
    reset();
  };

  // Prevent default touch behaviors
  const handleTouchCancel = (): void => {
    state.isDragging = false;
    state.startDistance = 0;
  };

  // Register event listeners
  element.addEventListener('touchstart', handleTouchStart, { passive: true });
  element.addEventListener('touchmove', handleTouchMove, { passive: false });
  element.addEventListener('touchend', handleTouchEnd);
  element.addEventListener('touchcancel', handleTouchCancel);
  element.addEventListener('wheel', handleWheel, { passive: false });
  element.addEventListener('mousedown', handleMouseDown);
  element.addEventListener('dblclick', handleDoubleClick);

  // Cleanup
  return () => {
    element.removeEventListener('touchstart', handleTouchStart);
    element.removeEventListener('touchmove', handleTouchMove);
    element.removeEventListener('touchend', handleTouchEnd);
    element.removeEventListener('touchcancel', handleTouchCancel);
    element.removeEventListener('wheel', handleWheel);
    element.removeEventListener('mousedown', handleMouseDown);
    element.removeEventListener('dblclick', handleDoubleClick);

    // Also clean up window listeners in case drag is still active
    window.removeEventListener('mousemove', handleMouseMove);
    window.removeEventListener('mouseup', handleMouseUp);
  };
});

export default panZoom;
